import { useTranslation } from 'react-i18next';
import React, {useState, useEffect } from "react";
import Markdown from 'react-markdown'
import { useNavigate, useParams } from "react-router";
import Api from "../../services/Api";
import LocalBreadcrumbs from "../../components/Breadcrumbs/LocalBreadcrumbs";
import {ClientInterface} from "../../interfaces/common";
import {getLanguage} from "../../lang/helpers";
import { CalendarWeek as CalendarIcon } from "flowbite-react-icons/outline";
import LearningIcon from "../../components/Icons/LearningIcon";
import AccountIcon from "../../components/Icons/AccountIcon";
import {zDate, dateFormatHourMinute, addMinutes, formatDateYear} from "../../helpers/dateFunctions";
import TextLink from "../../components/TextLink";

interface TaskInterface {
  id: string
  name: string
  description: string
  deadline: string
  focusAreaId: string
  focusAreaName: string
}

interface MeetingReflectionsInterface {
  notes: string
  images: Array<string>
}

interface MeetingSummaryInterface {
  id: string
  date: string
  length: number
  tasks: Array<TaskInterface>
  meetingReflections: MeetingReflectionsInterface | null
}

export default function MeetingSummary() {

  const api = Api.getInstance();
  const navigate = useNavigate();
  const translation = useTranslation();
  const { t, i18n } = translation;
  const lang = getLanguage(i18n.language);
  const { clientId } = useParams();
  const { meetingId } = useParams();
  const [client, setClient] = useState<ClientInterface>()
  const [meetingSummary, setMeetingSummary] = useState<MeetingSummaryInterface>()
  const [loaded, setLoaded] = useState<boolean>(false)

  useEffect(() => {
    api.get('coach/client/' + clientId + '/meeting_summary/' + meetingId).then((result) => {
      setClient(result.json.client)
      setMeetingSummary(result.json.meetingSummary)
      setLoaded(true)
    })
  }, [])

  if (!loaded) {
    return (<div className="mt-4">Loading meeting summary...</div>)
  }

  const crumbs = [
    { name: t('my-clients'), url: '/clients'},
    { name: client?.firstName + " " + client?.lastName, url: '/clients/' + clientId },
    { name: lang === 'sv' ? 'Mötessammanfattning' : 'Meeting summary'}
  ]

  const renderMeetingDate = () => {
    if (meetingSummary) {
      const d = new Date(meetingSummary.date)
      let dateStr = zDate(d, lang) + ", "
      dateStr += dateFormatHourMinute(d) + "-"
      const end = addMinutes(d, meetingSummary.length)
      dateStr += dateFormatHourMinute(end)
      return dateStr
    }
    return "N/A"
  }

  const renderMeetingSummary = ()=> {
    if (!meetingSummary) return;
    if (meetingSummary.tasks.length === 0 && !meetingSummary.meetingReflections) {
      return (
        <div className="mt-10 text-center">
          <CalendarIcon className="h-8 w-8 mx-auto" aria-hidden="true"/>
          <p className="mt-2 text-neutral-400">
            {
              lang === 'sv' ? 'Om ni använt coachverktyg under mötet sparas innehållet här.' :
                'If you used the coaching tools during the meeting, the content is saved here.'
            }
          </p>
        </div>
      )
    }

    return renderTaskAndReflections()

  }

  const renderTasks = () => {

    return (
      <>
        <div className="mt-6 text-lg font-medium">
          {
            lang === 'sv' ? 'Skapade aktiviteter' : 'Created actions'
          }
        </div>
        {
          meetingSummary?.tasks.map(task => (
            <div key={"task_" + task.id} className="mt-2 bg-neutral-700 rounded-md p-4">
              <p className="text-lg font-medium">{task.name}</p>

              <div className="flex mt-2 items-center">
                <CalendarIcon className="h-4 w-4" aria-hidden="true" />
                <div className="text-sm font-medium ml-1">{t('target-date')}:</div>
                <div className="text-sm text-gray-400 ml-1">
                  {
                    task.deadline ? (
                      formatDateYear(task.deadline)
                    ) : (
                      "-"
                    )
                  }
                </div>
              </div>

              {
                task.focusAreaName &&
                  <div className="flex mt-3 items-center">
                      <LearningIcon alt="learning" className="h-4 w-4"/>
                      <div className="text-sm font-medium ml-1">{t('focus-area')}:</div>
                      <div className="text-sm text-gray-400 ml-1">
                          <TextLink
                              size="sm"
                              onClick={() => navigate('/clients/' + client?.id + '/focus_area/' + task.focusAreaId)}>
                            {task.focusAreaName}
                          </TextLink>
                      </div>
                  </div>
              }

              <hr className="mt-4 border-neutral-600"/>

              <div className="mt-4 markdown-content">
                <Markdown>
                  {task.description}
                </Markdown>
              </div>
            </div>
          ))
        }
        {
          meetingSummary?.tasks.length === 0 &&
            <p className="mt-4 text-neutral-400">
              {
                lang === 'sv' ? 'Ni har inte skapat några aktiviteter under mötet.' :
                  'You haven\'t created any Actions during the meeting'
              }
            </p>
        }
      </>
    )
  }

  const renderReflections = () => {
    return (
      <>
        <div className="mt-6 text-lg font-medium">
          {
            lang === 'sv' ? 'Reflektionsyta' : 'Reflection space'
          }
        </div>
        {
          meetingSummary?.meetingReflections ? (
            <>
              {
                meetingSummary.meetingReflections.notes.length > 0 &&
                  <div className="mt-2 text-neutral-400 whitespace-nowrap">
                      &quot;{meetingSummary.meetingReflections.notes}&quot;
                  </div>
              }

              {
                meetingSummary.meetingReflections.images.map(nr => (
                  <img alt="pic" src={"https://public.zebrain.se/visionboard/pictures/" + nr + ".jpeg"}
                       key={"img_" + nr} className="mt-2 rounded-md"/>
                ))
              }
            </>
          ) : (
            <p className="mt-4 text-neutral-400">
              {
                lang === 'sv' ? 'Ni har inte reflektionsytan under mötet.' :
                  'You haven\'t used Reflection space during the meeting'
              }
            </p>
          )
        }
      </>
    )
  }

  const renderTaskAndReflections = () => {

    return (
      <>
        {renderTasks()}
        {renderReflections()}
      </>
    )

  }

  return (
    <div className="main-content">
      <LocalBreadcrumbs crumbs={crumbs}/>
      <div className="mt-6 text-2xl font-medium">
        {lang === 'sv' ? 'Mötessammanfattning' : 'Meeting summary'}
      </div>

      <div className="mt-6 flex items-center text-sm font-medium">
        <CalendarIcon className="h-4 w-4" aria-hidden="true" />
        <div className="ml-2">
          {renderMeetingDate()}
        </div>
        {client?.picture ? (
          <img className="ml-8 h-6 w-6 rounded-full" src={client.picture} alt="profile" />
        ) : (
          <AccountIcon className="ml-8 h-6 w-6 rounded-full" alt="profile" />
        )}
        <div className="ml-2">
          {client?.firstName} {client?.lastName}
        </div>
      </div>

      {renderMeetingSummary()}
    </div>
  )
};